import type { CSSProperties } from "react";
import React, { createElement, useMemo } from "react";
import cn from "classnames";

import type { IAvatarAuthor, IAvatarUser, TAvatarProps } from "./types";
import { AvatarType } from "./types";
import { getAuthorImageURL, getUserImageURL } from "./helpers";

import noPhoto24 from "./icons/Avatar/noPhoto24.svg";
import noPhoto40 from "./icons/Avatar/noPhoto40.svg";
import noPhoto68 from "./icons/Avatar/noPhoto68.svg";
import noPhoto100 from "./icons/Avatar/noPhoto100.svg";

import styles from "./Avatar.module.scss";
import { Link } from "../../link";

export const Avatar = ({
  className,
  onClick,
  contentTop,
  contentBottom,
  imgAnchor,
  e2eId,
  tabIndex,
  ...props
}: TAvatarProps) => {
  return (
    <div
      className={cn(
        styles.wrapper,
        { [styles.onClick]: Boolean(onClick) },
        className
      )}
      onClick={onClick}
      data-testid={e2eId}
      tabIndex={tabIndex}
      role="presentation"
    >
      {createElement(
        imgAnchor ? Link : React.Fragment,
        imgAnchor
          ? {
              href: imgAnchor,
              className: styles.onClick,
              onClick: (e: React.MouseEvent) => {
                e.preventDefault();
                window.location.href = imgAnchor;
              },
            }
          : null,
        <AvatarImg {...props} />
      )}
      {(contentTop || contentBottom) && (
        <div className={styles.content}>
          <div className={styles.topContent}>{contentTop}</div>
          <div className={styles.bottomContent}>{contentBottom}</div>
        </div>
      )}
    </div>
  );
};

const AvatarImg = (props: IAvatarUser | IAvatarAuthor) => {
  const cdnHost = "https://cdn.litres.ru";

  const calculateSize = props.containerSize || props.size;

  const avatarUrl = useMemo(() => {
    const getUrl =
      props.type === AvatarType.Author ? getAuthorImageURL : getUserImageURL;
    return getUrl(props.id, props.size, props.removeSizeFromImgUrl);
  }, [props.id, props.removeSizeFromImgUrl, props.size, props.type]);

  const noPhoto = useMemo(() => {
    if (props.type === AvatarType.User) {
      if (calculateSize > 68) {
        return noPhoto100;
      } else if (calculateSize > 40) {
        return noPhoto68;
      } else if (calculateSize > 24) {
        return noPhoto40;
      } else {
        return noPhoto24;
      }
    }
    return noPhoto100;
  }, [props.type, calculateSize]);

  const imagePath = useMemo(() => {
    if (props.imageURL) {
      return props.removeSizeFromImgUrl
        ? `${cdnHost}${props.imageURL}`
        : `${cdnHost}${props.imageURL?.replace(String(props.id), String(props.id) + `_${props.size}`)}`;
    } else if (props.hasAvatar) {
      return props.hash ? `${avatarUrl}?v=${props.hash}` : avatarUrl;
    } else {
      return noPhoto;
    }
  }, [props.imageURL, props.hasAvatar, props.hash, avatarUrl, noPhoto]);

  return (
    <img
      src={imagePath}
      style={
        {
          width: calculateSize,
          height: calculateSize,
          "--fallbackBg": `url(${noPhoto})`,
        } as CSSProperties
      }
      className={cn(styles.avatar, props.imgClassName)}
      loading={props.loading}
      decoding={props.decoding}
      alt={props.alt}
      aria-hidden="true"
    />
  );
};

export default Avatar;
